import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../../components/ui/button';
import { Maximize2, X, Map as MapIcon } from 'lucide-react';
import { MapLocation } from './types';

// Fix Leaflet default marker icon issue
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)['_getIconUrl'];
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function FitBounds({ locations }: { locations: MapLocation[] }) {
  const map = useMap();
  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations.map((l) => [l.lat, l.lng]));
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 6 });
    }
  }, [locations, map]);
  return null;
}

interface SupplierMapProps {
  locations: MapLocation[];
  onViewDetails: (id: number) => void;
}

export function SupplierMap({ locations, onViewDetails }: SupplierMapProps) {
  // Full-screen view (Barratt review ask): the inline map is 256px tall,
  // too small to pick out one of 3,500 suppliers. Expanded renders a second
  // MapContainer in a fixed overlay so Leaflet sizes itself to the viewport;
  // the map card can also be hidden entirely.
  const [expanded, setExpanded] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExpanded(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [expanded]);

  const map = (
    <MapContainer
      center={[30, 10]}
      zoom={2}
      style={{ height: '100%', width: '100%' }}
      scrollWheelZoom={expanded}
    >
      <FitBounds locations={locations} />
      {/* CARTO's raster basemaps now require an API key and render "API KEY
          REQUIRED" tiles without one; the OSM standard tiles need none. */}
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {locations.map((location) => (
        <Marker key={location.id} position={[location.lat, location.lng]}>
          <Popup>
            <div className="font-medium">{location.name}</div>
            <Button
              size="sm"
              variant="link"
              className="p-0 h-auto"
              onClick={() => onViewDetails(location.id)}
            >
              View Details
            </Button>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );

  if (hidden) {
    return (
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => setHidden(false)} data-testid="supplier-map-show">
          <MapIcon className="h-4 w-4 mr-2" />
          Show map
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-tour="supplier-map">
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => setExpanded(true)} data-testid="supplier-map-expand">
          <Maximize2 className="h-4 w-4 mr-2" />
          Full view
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setHidden(true)} aria-label="Hide map" data-testid="supplier-map-hide">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="h-64 rounded-lg overflow-hidden border relative z-0">
        {!expanded && map}
      </div>
      {expanded && (
        <div className="fixed inset-0 z-[60] bg-background flex flex-col" role="dialog" aria-label="Supplier map" data-testid="supplier-map-full">
          <div className="flex items-center justify-between px-4 py-2 border-b">
            <span className="font-medium">Supplier map · {locations.length} locations</span>
            <Button variant="ghost" size="sm" onClick={() => setExpanded(false)} aria-label="Close full view" data-testid="supplier-map-close">
              <X className="h-4 w-4 mr-2" />
              Close
            </Button>
          </div>
          <div className="flex-1 relative z-0">{map}</div>
        </div>
      )}
    </div>
  );
}
